import ImageBgLoading from '@component/imageBgLoading'
import houseSearchPageFrom from '@pageFrom/house'
import { houseDetail } from '@router'
import { getFullPath } from '@utils'
import { getHouseOpenhomeLabel } from '@utils/house'
import classNames from 'classnames'
import { map, range } from 'lodash-es'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { useNumber } from 'react-use'

const SlideItem = forwardRef(function SlideItem({
  image,
  width,
  height,
  imgClassName,
  alt,
  idx,
  isLast = false,
  hasOpenhome = false,
  handleViewDetail
}, ref) {
  return (
    <div className='w-full h-full snap-start shrink-0 relative pointer-events-none' key={idx} ref={ref}>
      <ImageBgLoading
        src={image}
        width={width}
        height={height}
        className={classNames('w-full h-full', imgClassName)}
        alt={`${alt}_${idx}`}
        loading='lazy'
      />
      {
        isLast && (
          <div
            data-noprogress
            className={classNames(
              'absolute inset-0 grid place-items-center cursor-auto pointer-events-auto',
              { 'bg-black bg-opacity-50': !hasOpenhome }
            )}
            onClick={(event) => event.preventDefault()}
          >
            <button
              type='button'
              className='bg-white px-24 h-40 flex items-center text-primary rounded-[100px] shadow-md relative z-[4]'
              onClick={handleViewDetail}
            >
              view more
            </button>
          </div>
        )
      }
    </div>
  )
})

export default function Slide({
  data,
  images = [],
  width,
  height,
  imgClassName,
  alt,
  children
}) {
  const containerRef = useRef(null)
  // const virtualRef = useRef(null)
  const [index, { set: setIndex }] = useNumber(0)

  // 跳转到下一张
  const handleNext = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()

    const slides = Array.from(containerRef.current.children)
    const nextSlide = slides[index + 1]
    if (nextSlide) {
      nextSlide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
    }
  }, [index])

  // 跳转到上一张
  const handlePrev = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()

    const slides = Array.from(containerRef.current.children)
    const prevSlide = slides[index - 1]
    if (prevSlide) {
      prevSlide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
    }
  }, [index])

  // 跳转到详情页
  const handleViewDetail = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    houseSearchPageFrom.serpListingClick(data)
    window.open(getFullPath(houseDetail(data)), '_blank')
  }, [data])

  // 在滚动时，切换当前显示图片的索引
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const childrens = Array.from(container.children)
    const observer = new IntersectionObserver((entries) => {
      let idx = -1
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          idx = Array.from(container.children).indexOf(entry.target)
        }
      })

      if (idx >= images.length) {
        idx = 0
      }

      if (idx !== -1) {
        setIndex(idx)
      }
    }, {
      root: container,
      rootMargin: '0px',
      threshold: 0.5
    })

    childrens.forEach((child) => {
      observer.observe(child)
    })

    return () => {
      observer.disconnect()
    }
  }, [setIndex, images])

  const hasOpenhome = Boolean(getHouseOpenhomeLabel(data))
  return (
    <div className='w-full h-full relative overflow-hidden touch-none bg-white group/slide'>
      <div
        className='flex w-full h-full overflow-x-scroll relative snap-x snap-mandatory scrollbar-none space-x-10'
        ref={containerRef}
      >
        {
          map(images, (image, idx) => (
            <SlideItem
              key={idx}
              image={image}
              width={width}
              height={height}
              imgClassName={imgClassName}
              alt={alt}
              idx={idx}
              isLast={idx === images.length - 1 && images.length >= 6}
              hasOpenhome={hasOpenhome}
              handleViewDetail={handleViewDetail}
            />
          ))
        }
      </div>
      {
        images.length >= 6 && (
          <>
            <SlidePagination index={index} length={images.length} />
            <SlideButton onClick={handlePrev} disabled={index === 0} left />
            <SlideButton onClick={handleNext} disabled={index === images.length - 1} right />
          </>
        )
      }
      {children}
    </div>
  )
}


function SlideButton({
  left = false,
  right = false,
  disabled = false,
  onClick
}) {
  if (disabled) {
    return null
  }

  return (
    <button
      data-noprogress
      className={classNames(
        'absolute top-1/2 -translate-y-1/2 w-32 h-32 z-[4] bg-white rounded-half place-items-center sm:opacity-0 group-hover/slide:opacity-100 transition-opacity duration-300 ease-in-out hidden sm:grid',
        { 'left-14': left, 'right-14': right }
      )}
      onClick={onClick}
    >
      {left && <i className='icon icon-arrow-left pointer-events-none' />}
      {right && <i className='icon icon-arrow-right pointer-events-none' />}
    </button>
  )
}

export function SlidePagination({
  index,
  length,
}) {
  const [offsetX, setOffsetX] = useState(0)
  const wapperRef = useRef(null)
  const containerRef = useRef(null)

  useEffect(() => {
    const wapper = wapperRef.current
    const container = containerRef.current

    if (!wapper || !container) return

    const centreX = wapper.offsetWidth / 2
    const currentDot = container.children[index]
    if (currentDot) {
      const currentX = currentDot.offsetLeft + currentDot.offsetWidth / 2
      if (currentX >= centreX && container.offsetWidth - currentX >= centreX) {
        setOffsetX(currentX - centreX)
      } else if (currentDot.offsetLeft === 0) {
        setOffsetX(0)
      }
    }

  }, [index, setOffsetX, offsetX])

  return (
    <div className='absolute inset-x-0 bottom-10 flex justify-center z-[4]'>
      <div className='w-92 bg-[#BFBFBF] bg-opacity-60 backdrop-blur-xl rounded-3xl px-10'>
        <div className='overflow-hidden w-full' ref={wapperRef}>
          <div
            className='w-fit flex gap-x-8 py-5 transition-transform will-change-transform'
            style={{ transform: `translateX(${-offsetX}px)` }}
            ref={containerRef}
          >
            {
              map(range(length), (_, idx) => (
                <span key={idx} className='w-8 h-8 grid place-items-center shrink-0 snap-start'>
                  <i
                    className={classNames(
                      'w-full h-full bg-white rounded-half transition-all ease-in-out will-change-auto',
                      { 'opacity-30': idx !== index },
                      idx !== index && (
                        Math.abs(idx - index) <= 1
                          ? 'scale-[0.8]'
                          : 'scale-[0.65]'
                      )
                    )}
                  />
                </span>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  )
}